/**
 * LanguageSwitcher — portál nyelvválasztó komponens.
 *
 * Két változat:
 *  - <LanguageSwitcher /> drawer-style (hamburger menüből)
 *  - <LanguageSwitcherCard /> teljes szekció kártya (profil oldalra)
 */
import { useTranslation } from "react-i18next";
import { Check, Globe } from "lucide-react";
import { setPortalLanguage, PORTAL_LANGUAGES } from "@/portal/i18n";

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  return (
    <div
      data-testid="lang-switcher"
      className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900"
    >
      <div className="flex items-center gap-2 border-b border-zinc-100 px-4 py-2.5 dark:border-zinc-800">
        <Globe className="h-3.5 w-3.5 text-zinc-400" strokeWidth={2} />
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
          {t("language.select")}
        </p>
      </div>
      <ul>
        {PORTAL_LANGUAGES.map((lng) => {
          const active = i18n.language === lng.code;
          return (
            <li key={lng.code}>
              <button
                type="button"
                data-testid={`lang-${lng.code}`}
                onClick={() => setPortalLanguage(lng.code)}
                className={
                  "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors " +
                  (active
                    ? "bg-blue-50/60 dark:bg-blue-950/30"
                    : "hover:bg-zinc-50 dark:hover:bg-zinc-800/60")
                }
              >
                <span className="text-[18px]" aria-hidden>
                  {lng.flag}
                </span>
                <span
                  className={
                    "flex-1 text-[14px] " +
                    (active
                      ? "font-semibold text-blue-700 dark:text-blue-300"
                      : "font-medium text-zinc-700 dark:text-zinc-200")
                  }
                >
                  {lng.label}
                </span>
                {active && (
                  <Check className="h-4 w-4 text-blue-500" strokeWidth={2.5} />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function LanguageSwitcherCard() {
  const { i18n, t } = useTranslation();
  const current = PORTAL_LANGUAGES.find((l) => l.code === i18n.language) || PORTAL_LANGUAGES[0];
  return (
    <section
      data-testid="lang-switcher-card"
      className="rounded-2xl bg-white p-4 shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900"
    >
      <header className="mb-3 flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300">
          <Globe className="h-3.5 w-3.5" strokeWidth={2} />
        </span>
        <div>
          <p className="text-[13.5px] font-semibold tracking-tight">
            {t("profile.language_setting")}
          </p>
          <p className="text-[11.5px] text-zinc-500">{current.label}</p>
        </div>
      </header>
      <div className="grid grid-cols-3 gap-2">
        {PORTAL_LANGUAGES.map((lng) => {
          const active = i18n.language === lng.code;
          return (
            <button
              key={lng.code}
              type="button"
              data-testid={`profile-lang-${lng.code}`}
              onClick={() => setPortalLanguage(lng.code)}
              className={
                "flex flex-col items-center gap-1 rounded-xl py-3 transition-all " +
                (active
                  ? "bg-blue-50 ring-2 ring-blue-500 dark:bg-blue-950/30"
                  : "bg-zinc-50 hover:bg-zinc-100 dark:bg-zinc-800/40 dark:hover:bg-zinc-800")
              }
            >
              <span className="text-[22px]" aria-hidden>
                {lng.flag}
              </span>
              <span
                className={
                  "text-[11.5px] " +
                  (active
                    ? "font-semibold text-blue-700 dark:text-blue-300"
                    : "font-medium text-zinc-600 dark:text-zinc-300")
                }
              >
                {lng.label}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
